<template>
    <div class="hts-menu">
        <div
            v-if="!loading"
            id="menu"
            class="hts-menu__container h-52 relative">
            <div
                class="absolute hidden bg-white top-2 right-[-14px] lg:flex items-center justify-center cursor-pointer h-7 w-7 rounded-full border"
                @click="changeMiniVariantIcon">
                <hi-chevron-double-left-icon
                    class="h-5 w-5 transition-all duration-300 ease-in-out flex-shrink-0 text-gray-500 hover:text-gray-700"
                    aria-hidden="true"
                    :class="{ 'rotate-180': miniVariant }" />
            </div>

            <div class="h-7" />
            <div class="w-full h-full overflow-y-auto overflow-x-hidden pb-3">
                <div
                    v-for="(item, index) in menuItems"
                    :key="index">
                    <div v-if="checkPermission(item.permissions, item.justOnePermission || false)">
                        <template v-if="item.children">
                            <div
                                class="w-full flex items-center border-t pl-3 py-2 font-medium focus:outline-none focus:ring-0 cursor-pointer"
                                @mouseover="(event) => handleMouseOverMenu(event, item.children)"
                                @mouseleave="handleLeaveMouseOverMenu">
                                <hts-icons
                                    :icon-name="item.icon"
                                    size="large"
                                    class="mr-3 flex-shrink-0" />
                                <span
                                    class="flex-1 whitespace-nowrap transition-all duration-300 ease-in-out"
                                    :class="{ 'opacity-0 w-0': !showName, 'opacity-100': showName }">
                                    {{ $t(item.name) }}
                                </span>
                            </div>

                            <div
                                class="mb-4 border-b transition-all duration-300 ease-in-out overflow-x-hidden"
                                :class="{ 'max-h-0': !showName, 'max-h-full': showName }">
                                <div
                                    v-for="(child, childIndex) in item.children"
                                    :key="childIndex">
                                    <template v-if="child.route">
                                        <hts-link
                                            v-show="checkPermission(child.permissions, item.justOnePermission || false)"
                                            :to="getRoute(child)"
                                            :active="checkActive(child)"
                                            class="w-full flex items-center pl-6 py-2 font-medium focus:outline-none focus:ring-0 cursor-pointer">
                                            <hts-icons
                                                :icon-name="child.icon"
                                                size="large"
                                                class="mr-3 flex-shrink-0" />
                                            <span
                                                class="whitespace-nowrap transition-all duration-500 ease-in-out"
                                                :class="{ 'opacity-0': !showName, 'opacity-100': showName }">
                                                {{ $t(child.name) }}
                                            </span>
                                        </hts-link>
                                    </template>
                                </div>
                            </div>
                        </template>
                        <template v-else>
                            <hts-link
                                :to="getRoute(item)"
                                :active="checkActive(item)"
                                class="w-full flex items-center mb-3 pl-3 py-2 font-medium focus:outline-none focus:ring-0 cursor-pointer">
                                <hts-icons
                                    :icon-name="item.icon"
                                    size="large"
                                    class="mr-3 flex-shrink-0" />
                                <span
                                    class="flex-1 whitespace-nowrap transition-all duration-300 ease-in-out"
                                    :class="{ 'opacity-0': !showName, 'opacity-100': showName }">
                                    {{ $t(item.name) }}
                                </span>
                            </hts-link>
                        </template>
                    </div>
                </div>
            </div>

            <div
                v-show="miniVariant"
                id="subMenu"
                ref="subMenu"
                style="display: none"
                class="w-[300px] absolute right-[-310px] rounded-2xl z-auto bg-default-100 transition-all duration-300"
                @mouseover="handleMouseOverSubMenu"
                @mouseleave="handleLeaveMouseOverMenu">
                <div
                    v-for="(child, childIndex) in subMenuData"
                    :key="childIndex"
                    class="transition-all rounded-2xl hover:bg-gray-200">
                    <template v-if="child.route">
                        <hts-link
                            v-show="checkPermission(child.permissions, item.justOnePermission || false)"
                            :to="getRoute(child)"
                            :active="checkActive(child)"
                            class="w-full flex items-center pl-6 py-2 font-medium focus:outline-none focus:ring-0 cursor-pointer">
                            <hts-icons
                                :icon-name="child.icon"
                                size="large"
                                class="mr-3 flex-shrink-0" />
                            <span class="whitespace-nowrap transition-all duration-300 ease-in-out">
                                {{ $t(child.name) }}
                            </span>
                        </hts-link>
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { checkPermission } from '@/utils/permission-check'

const props = defineProps({
  menuItems: {
    type: Array<Object>,
    required: true
  }
})
const { menuItems } = toRefs(props)
const store = $store()
const route = useRoute()
const router = useRouter()
const id = computed(() => router.currentRoute.value.params.id)
const isAdmin = computed(() => store.userConfig.isAdmin)
const workspace: any = computed(() => store.system.workspace)
const isMenuUser = ref(false)
const refMenuUser = ref(null)
const refImgMenuUser = ref(null)
const username = ref('')
const avatarUrl = ref('')
const { isOutside } = useMouseInElement(refImgMenuUser)
const routeName = computed<string>(() => useRoute().name as string)
const timeOutCLoseSubMenuId = ref<any>(0)
const subMenu = ref()
const subMenuData = ref<{ [key: string]: string }[]>([])
const loading = ref(false)

onMounted(() => {
  const userInfo = store.userConfig.getUser
  username.value = userInfo.fullName || ''
  if (userInfo.avatar) {
    avatarUrl.value = userInfo.avatar
  } else {
    avatarUrl.value = '/assets/default-avatar.png'
  }
})

const handleCheckPermission = (key: string) => {
  if (!id) {
    return checkPermission(key)
  }
}

/** Methods */
const getRoute = (item: any): {} | null => {
  if (!item.route) {
    return null
  }
  if (!id.value) {
    if (item.route.name.includes('id')) {
      return null
    }

    return {
      ...item.route
    }
  }
  // kiểm tra page có tabs thì chuyển hướng đến tab đầu tiên có quyền truy cập
  // tabs đc cài đặt trong  menu data
  if (item.tabChildren) {
    for (const tab of item.tabChildren) {
      if (checkPermission(tab.permissions)) {
        return {
          ...tab.route
        }
      }
    }
  }
  return {
    ...item.route,
    params: { id: id.value }
  }
}

const showName = computed(() => {
  if (store.system.miniVariant) {
    return false
  }

  return true
})

const handleMouseOverMenu = (event: any, data?: any) => {
  if (!event || !subMenu?.value || !miniVariant.value) {
    return
  }
  clearTimeout(timeOutCLoseSubMenuId.value)

  subMenuData.value = data || []
  subMenu.value.style.top = `${event.clientY - 70}px`
  subMenu.value.style.display = 'block'
}

const handleMouseOverSubMenu = (event: any) => {
  clearTimeout(timeOutCLoseSubMenuId.value)
}

const handleLeaveMouseOverMenu = () => {
  timeOutCLoseSubMenuId.value = setTimeout(() => {
    subMenu.value.style.display = 'none'
    subMenuData.value = []
  }, 500)
}

const miniVariant = computed(() => {
  if (store.system.miniVariant) {
    return true
  }
  return false
})

const changeMiniVariantIcon = () => {
  if (miniVariant.value) {
    store.system.miniVariant = false
    return
  }

  store.system.miniVariant = true
}

const setIsMenuUser = () => {
  isMenuUser.value = !isMenuUser.value
}

onClickOutside(refMenuUser, () => {
  if (isOutside.value) {
    isMenuUser.value = false
  }
})
const isActive = (item: any): string => {
  const routeName = item.route.name
  if (routeName === route.name) {
    return 'menu-item-active'
  }
  return ''
}
const checkActive = (item: { [key: string]: any }) => {
  if (!item.group || typeof routeName.value !== 'string') {
    return false
  }
  return routeName.value.includes(item.group)
}
</script>
